import React, { useState, useEffect } from 'react';
import { Users, CheckCircle, XCircle, Search, Shield, Building2, User } from 'lucide-react';

const API = 'https://talentflow-backend-production-09dd.up.railway.app/api';

export default function UserManagement({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [msg, setMsg] = useState('');

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const r = await fetch(`${API}/auth/users`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setUsers(d.users || []);
    } catch {}
    setLoading(false);
  };

  const toggleUser = async (userId, isActive) => {
    try {
      const r = await fetch(`${API}/auth/toggle-user/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isActive: !isActive })
      });
      const d = await r.json();
      if (d.success) {
        setMsg(`User ${!isActive ? 'activated' : 'blocked'} successfully!`);
        loadUsers();
        setTimeout(() => setMsg(''), 3000);
      }
    } catch {}
  };

  const filtered = users.filter(u => {
    const matchSearch = u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || u.role === filter;
    return matchSearch && matchFilter && u.role !== 'admin';
  });

  const stats = [
    { label: 'Total Users', value: users.filter(u => u.role !== 'admin').length, icon: Users, color: '#6366f1', bg: '#ede9fe' },
    { label: 'Clients', value: users.filter(u => u.role === 'client').length, icon: Building2, color: '#16a34a', bg: '#dcfce7' },
    { label: 'Vendors', value: users.filter(u => u.role === 'vendor').length, icon: User, color: '#f59e0b', bg: '#fef9c3' },
    { label: 'Blocked', value: users.filter(u => !u.isActive && u.role !== 'admin').length, icon: XCircle, color: '#ef4444', bg: '#fee2e2' },
  ];

  const roleIcon = (role) => role === 'client' ? '🏢' : role === 'vendor' ? '👥' : '⚡';
  const colors = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981'];
  const avatarColor = (name) => colors[name?.charCodeAt(0) % colors.length];

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>Loading users...</div>;

  return (
    <div>
      {msg && <div style={{ padding: '12px 16px', background: '#dcfce7', color: '#16a34a', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>{msg}</div>}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '16px', marginBottom: '24px' }}>
        {stats.map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                <Icon size={20} color={s.color} />
              </div>
              <div style={{ color: '#1e293b', fontSize: '28px', fontWeight: '700', marginBottom: '4px' }}>{s.value}</div>
              <div style={{ color: '#94a3b8', fontSize: '13px' }}>{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Search + Filter */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '200px', display: 'flex', alignItems: 'center', gap: '10px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 14px' }}>
          <Search size={15} color="#94a3b8" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..."
            style={{ background: 'none', border: 'none', color: '#1e293b', fontSize: '14px', outline: 'none', width: '100%' }} />
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {[['all', 'All'], ['client', 'Clients'], ['vendor', 'Vendors']].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)}
              style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit',
                background: filter === val ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : '#f1f5f9',
                color: filter === val ? 'white' : '#64748b' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['USER', 'ROLE', 'EMAIL', 'JOINED', 'STATUS', 'ACTION'].map(h => (
                <th key={h} style={{ padding: '12px 20px', textAlign: 'left', color: '#94a3b8', fontSize: '11px', fontWeight: '600' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u._id} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={{ padding: '14px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: avatarColor(u.name), display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700', fontSize: '14px', flexShrink: 0 }}>
                      {u.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ color: '#1e293b', fontWeight: '600', fontSize: '14px' }}>{u.name}</div>
                      {u.companyName && <div style={{ color: '#94a3b8', fontSize: '12px' }}>{u.companyName}</div>}
                    </div>
                  </div>
                </td>
                <td style={{ padding: '14px 20px' }}>
                  <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
                    background: u.role === 'client' ? '#dcfce7' : '#fef9c3',
                    color: u.role === 'client' ? '#16a34a' : '#ca8a04' }}>
                    {roleIcon(u.role)} {u.role?.toUpperCase()}
                  </span>
                </td>
                <td style={{ padding: '14px 20px', color: '#64748b', fontSize: '13px' }}>{u.email}</td>
                <td style={{ padding: '14px 20px', color: '#64748b', fontSize: '13px' }}>
                  {new Date(u.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </td>
                <td style={{ padding: '14px 20px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
                    background: u.isActive ? '#dcfce7' : '#fee2e2',
                    color: u.isActive ? '#16a34a' : '#dc2626' }}>
                    <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: u.isActive ? '#16a34a' : '#dc2626' }} />
                    {u.isActive ? 'ACTIVE' : 'BLOCKED'}
                  </span>
                </td>
                <td style={{ padding: '14px 20px' }}>
                  <button onClick={() => toggleUser(u._id, u.isActive)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: 'inherit',
                      background: u.isActive ? '#fee2e2' : '#dcfce7',
                      color: u.isActive ? '#dc2626' : '#16a34a' }}>
                    {u.isActive ? <><XCircle size={13}/> Block</> : <><CheckCircle size={13}/> Activate</>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
            <Users size={40} color="#e2e8f0" style={{ margin: '0 auto 12px', display: 'block' }} />
            <p style={{ margin: 0 }}>No users found</p>
          </div>
        )}
      </div>
    </div>
  );
}
